import AddIcon from '@mui/icons-material/Add';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ViewListIcon from '@mui/icons-material/ViewList';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { dayPartApi, dayPartKeys } from '@/api/account-management/dayPartApi';
import { locationGroupApi } from '@/api/account-management/locationGroupApi';
import { mediaNetworkApi } from '@/api/media-network/mediaNetworkApi';
import { mediaNetworkKeys } from '@/api/media-network/mediaNetworkQueries';
import { type DayPart } from '@/models/account-management/DayPart';
import { type Location } from '@/models/account-management/Location';
import { type LocationGroup } from '@/models/account-management/LocationGroup';
import { type Playlist } from '@/models/media-network/Playlist';
import {
    type PlaylistSchedule, PlaylistScheduleScope,
    RecurrenceFrequency, WEEKDAY_BITS, hasDay, minuteToTime,
} from '@/models/media-network/PlaylistSchedule';
import { reachesLocation } from '@/models/media-network/scheduleOccurrences';
import { useAppContext } from '../../AppContext';
import { useLocalizedStrings } from '../../localization/LocalizedStringsProvider';
import { usePermissions } from '../../permissions/usePermissions';
import { Perm } from '../../permissions/permissions';
import { useToast } from '../common/toast/ToastProvider';
import ScheduleCalendar from './ScheduleCalendar';
import ScheduleDialog from './dialogs/ScheduleDialog';
import { useAssignedLocations } from '../../permissions/useLocationScope';

// Every schedule across the brand's playlists, in one place. Schedules are stored per playlist and the API is
// keyed that way (`playlists/{id}/schedules`), so this fans out one query per playlist and flattens the
// result. Fine for the handful of playlists a brand has; if that stops being true, the fix is a brand-level
// list endpoint rather than more queries here.
const PlaylistSchedules = () => {
    const qc = useQueryClient();
    const toast = useToast();
    const strings = useLocalizedStrings();
    const app = useAppContext();
    const perms = usePermissions();
    const canCreate = perms.canCreate(Perm.mediaNetworkAdBanner);
    const canUpdate = perms.canUpdate(Perm.mediaNetworkAdBanner);
    const canDelete = perms.canDelete(Perm.mediaNetworkAdBanner);

    const brandId = app.brandId ?? undefined;

    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState<PlaylistSchedule | null>(null);
    const [view, setView] = useState<'list' | 'calendar'>('list');
    // Empty = every location. A brand with one schedule per store is unreadable as a single calendar, and
    // filtering by location is also the question people actually have: what runs at THIS store.
    const [filterLocationId, setFilterLocationId] = useState('');

    const { data: playlists } = useQuery<Playlist[]>({
        queryKey: mediaNetworkKeys.list(brandId ?? ''),
        enabled: !!brandId,
        queryFn: () => mediaNetworkApi.list(brandId!),
        staleTime: 0,
    });

    const scheduleQueries = useQueries({
        queries: (playlists ?? []).map((p) => ({
            queryKey: mediaNetworkKeys.schedules(p.id),
            queryFn: () => mediaNetworkApi.listSchedules(p.id),
            staleTime: 0,
        })),
    });

    // Names rather than raw ids for the targets, and the pickers in the dialog.
    // Only the locations this user is assigned to - schedule targeting offered the brand's whole estate (TTD-160).
    const { locations } = useAssignedLocations(brandId);
    const { data: groups } = useQuery<LocationGroup[]>({
        queryKey: ['location-groups', brandId ?? ''],
        enabled: !!brandId,
        queryFn: () => locationGroupApi.list(brandId!),
        staleTime: Infinity,
    });
    // Read under the PROMOTIONS permission, not configuration-day-parts: you have to see day parts to
    // schedule against them, and to render a schedule that already references one. Managing them is what
    // configuration-day-parts gates.
    const { data: dayParts } = useQuery<DayPart[]>({
        queryKey: dayPartKeys.list(brandId ?? ''),
        enabled: !!brandId,
        queryFn: () => dayPartApi.list(brandId!),
        staleTime: Infinity,
    });

    const { mutateAsync: remove } = useMutation({
        mutationFn: ({ playlistId, scheduleId }: { playlistId: string; scheduleId: string }) =>
            mediaNetworkApi.deleteSchedule(playlistId, scheduleId),
        onSuccess: async (_r, vars) => {
            toast({ severity: 'success', message: strings.playlistScheduleDeleted });
            await qc.invalidateQueries({ queryKey: mediaNetworkKeys.schedules(vars.playlistId) });
        },
        onError: () => toast({ severity: 'error', message: strings.playlistScheduleDeleteError }),
    });

    // Flatten, and carry the owning playlist's name so a schedule is identifiable without drilling in.
    const rows = (playlists ?? []).flatMap((p, index) =>
        (scheduleQueries[index]?.data ?? []).map((s) => ({ schedule: s, playlistId: p.id, playlistName: p.name })));

    // The calendar's rows, narrowed to one location when asked. A brand-targeted schedule reaches every
    // location, so filtering keeps far more than the schedules literally addressed to that store — which is
    // the point, since a brand promo not showing there is exactly what someone is trying to explain.
    const calendarRows = filterLocationId
        ? rows.filter((r) => reachesLocation(r.schedule, filterLocationId, groups ?? []))
        : rows;

    const targetName = (scope: PlaylistScheduleScope, targetId: string): string => {
        if (scope === PlaylistScheduleScope.Brand) return strings.playlistScopeEntireBrand;
        if (scope === PlaylistScheduleScope.LocationGroup) {
            return groups?.find(g => g.id === targetId)?.name ?? strings.playlistUnknownTarget;
        }
        return locations?.find(l => l.id === targetId)?.name ?? strings.playlistUnknownTarget;
    };

    // A one-line human summary of the window. Deliberately plain string formatting — these values are local
    // wall-clock and must never be run through a Date, which would attach the browser's timezone.
    const describeWindow = (s: PlaylistSchedule): string => {
        const dayPart = s.dayPartId ? dayParts?.find(d => d.id === s.dayPartId) : undefined;

        // Three window shapes, and "all day" is a real one rather than a missing pair of times — so say so
        // instead of rendering an empty "–".
        const window = dayPart
            ? `${dayPart.name} (${minuteToTime(dayPart.startMinute)}–${minuteToTime(dayPart.endMinute)})`
            : s.dayPartId
                // Points at a day part that no longer resolves; the schedule will not run.
                ? strings.playlistUnknownTarget
                : s.startMinute == null
                    ? strings.playlistWindowAllDay
                    : `${minuteToTime(s.startMinute)}–${minuteToTime(s.endMinute)}`;

        const every = s.recurrenceInterval && s.recurrenceInterval > 1 ? `${s.recurrenceInterval} ` : '';
        let repeat: string;
        if (s.recurrenceFrequency === RecurrenceFrequency.Weekly) {
            const days = WEEKDAY_BITS
                .map((bit, i) => (hasDay(s.recurrenceDaysOfWeek, bit) ? strings.weekdayShort.split(',')[i] : null))
                .filter(Boolean)
                .join(' ');
            repeat = `${strings.playlistEveryWeeks.replace('{{n}}', every)} ${days}`;
        } else if (s.recurrenceFrequency === RecurrenceFrequency.Monthly) {
            repeat = strings.playlistEveryMonths
                .replace('{{n}}', every)
                .replace('{{day}}', String(s.recurrenceDayOfMonth ?? ''));
        } else {
            repeat = strings.playlistEveryDays.replace('{{n}}', every);
        }

        return `${window} · ${repeat}`;
    };

    if (!brandId) {
        return <Typography variant="body2" color="text.secondary">{strings.playlistSelectBrandTopBar}</Typography>;
    }

    return (
        <Stack spacing={2} sx={{ height: '100%', minHeight: 0, overflow: 'auto' }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" useFlexGap spacing={1}>
                <Typography variant="h6">{strings.playlistSchedules}</Typography>

                <Stack direction="row" spacing={1} alignItems="center">
                    {view === 'calendar' && (
                        <FormControl size="small" sx={{ minWidth: 220 }}>
                            <InputLabel id="calendar-location-label">{strings.location}</InputLabel>
                            <Select
                                labelId="calendar-location-label"
                                label={strings.location}
                                value={filterLocationId}
                                onChange={(e) => setFilterLocationId(e.target.value)}
                            >
                                <MenuItem value="">{strings.playlistCalendarAllLocations}</MenuItem>
                                {(locations ?? []).map((l) => (
                                    <MenuItem key={l.id} value={l.id}>{l.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}

                    <ToggleButtonGroup
                        exclusive
                        size="small"
                        value={view}
                        onChange={(_e, next) => { if (next) setView(next); }}
                    >
                        <ToggleButton value="list" aria-label="list view">
                            <ViewListIcon fontSize="small" />
                        </ToggleButton>
                        <ToggleButton value="calendar" aria-label="calendar view">
                            <CalendarMonthIcon fontSize="small" />
                        </ToggleButton>
                    </ToggleButtonGroup>

                    {canCreate && (
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            disabled={!playlists?.length}
                            onClick={() => { setEditing(null); setDialogOpen(true); }}
                            size="input"
                        >
                            {strings.playlistNewSchedule}
                        </Button>
                    )}
                </Stack>
            </Stack>

            {/* A schedule needs a playlist to belong to, so say that rather than opening an unusable dialog. */}
            {!playlists?.length && (
                <Typography variant="body2" color="text.secondary">{strings.playlistNoPlaylistsYet}</Typography>
            )}

            {!!playlists?.length && rows.length === 0 && (
                <Typography variant="body2" color="text.secondary">{strings.playlistNoSchedules}</Typography>
            )}

            {view === 'calendar' && !!playlists?.length && (
                <ScheduleCalendar
                    entries={calendarRows}
                    dayParts={dayParts}
                    locationName={locations?.find((l) => l.id === filterLocationId)?.name}
                    // A chip is the fastest route to the thing it represents, and editing is where someone
                    // goes the moment they spot the overlap they came here to find.
                    onSelect={(s) => { setEditing(s); setDialogOpen(true); }}
                />
            )}

            {view === 'list' && rows.map(({ schedule: s, playlistName }) => (
                <Paper key={s.id} variant="outlined" sx={{ p: 1.5 }}>
                    <Stack direction="row" spacing={2} alignItems="flex-start">
                        <Stack sx={{ flex: 1, minWidth: 0 }} spacing={0.5}>
                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                <Typography variant="subtitle2">{playlistName}</Typography>
                                {/* The schedule-type chip is gone: every schedule recurs, so it said the same
                                    thing on every row. The window summary alongside carries the real detail. */}
                                {!s.enabled && <Chip size="small" variant="outlined" label={strings.disabled} />}
                                <Typography variant="body2">{describeWindow(s)}</Typography>
                            </Stack>

                            {/* Targets are listed rather than counted: which stores a promo runs at is the
                                thing people get wrong, and a bare number hides it. */}
                            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                {s.targets.map((t) => (
                                    <Chip
                                        key={t.id}
                                        size="small"
                                        variant="outlined"
                                        color={t.scope === PlaylistScheduleScope.Location ? 'primary' : 'default'}
                                        label={targetName(t.scope, t.targetId)}
                                    />
                                ))}
                            </Stack>

                            {s.recurrenceEndDate && (
                                <Typography variant="caption" color="text.secondary">
                                    {strings.playlistUntil.replace('{{date}}', s.recurrenceEndDate)}
                                </Typography>
                            )}
                        </Stack>

                        <Stack direction="row" spacing={0.5}>
                            {canUpdate && (
                                <IconButton size="small" onClick={() => { setEditing(s); setDialogOpen(true); }} aria-label="edit schedule">
                                    <EditIcon fontSize="small" />
                                </IconButton>
                            )}
                            {canDelete && (
                                <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => remove({ playlistId: s.playlistId, scheduleId: s.id })}
                                    aria-label="delete schedule"
                                >
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            )}
                        </Stack>
                    </Stack>
                </Paper>
            ))}

            <Box>
                <ScheduleDialog
                    open={dialogOpen}
                    brandId={brandId}
                    playlists={playlists ?? []}
                    schedule={editing}
                    locations={locations ?? []}
                    groups={groups ?? []}
                    dayParts={dayParts ?? []}
                    onClose={() => setDialogOpen(false)}
                />
            </Box>
        </Stack>
    );
};

export default PlaylistSchedules;
